"use client";

import React, { useRef, useState, useEffect } from 'react';
import { Wind, ShieldCheck, Droplets, Zap } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { motion } from 'framer-motion';
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = '0px'
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === 'undefined' || typeof IntersectionObserver === 'undefined') {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}

// CSS-based animated silk effect
function AnimatedSilk() {
  return <div className="absolute inset-0 flex items-center justify-center">
      <motion.div className="relative w-[600px] h-[600px]" animate={{
      rotateZ: [0, 5, 0, -5, 0]
    }} transition={{
      duration: 8,
      repeat: Infinity,
      ease: "easeInOut"
    }}>
        {/* Layered animated meshes */}
        {[0, 1, 2].map(layer => <motion.div key={layer} className="absolute inset-0 rounded-full opacity-30" style={{
        background: `radial-gradient(ellipse at center, 
                rgba(160, 196, 255, ${0.6 - layer * 0.15}) 0%, 
                rgba(96, 165, 250, ${0.4 - layer * 0.1}) 30%, 
                rgba(59, 130, 246, ${0.2 - layer * 0.05}) 60%, 
                transparent 100%)`,
        filter: 'blur(20px)',
        transform: `scale(${1 + layer * 0.2})`
      }} animate={{
        scale: [1 + layer * 0.2, 1.1 + layer * 0.2, 1 + layer * 0.2],
        rotateX: [0, 15, 0, -15, 0],
        rotateY: [0, 10, 0, -10, 0]
      }} transition={{
        duration: 6 + layer * 2,
        repeat: Infinity,
        ease: "easeInOut",
        delay: layer * 0.5
      }} />)}
        
        {/* Central silk wave effect */}
        <motion.div className="absolute inset-0 flex items-center justify-center" animate={{
        rotateY: [0, 180, 360]
      }} transition={{
        duration: 20,
        repeat: Infinity,
        ease: "linear"
      }}>
          <div className="w-[400px] h-[400px] rounded-full" style={{
          background: 'linear-gradient(135deg, rgba(160, 196, 255, 0.4) 0%, rgba(96, 165, 250, 0.3) 50%, rgba(59, 130, 246, 0.2) 100%)',
          boxShadow: '0 0 60px rgba(96, 165, 250, 0.5), inset 0 0 60px rgba(160, 196, 255, 0.3)'
        }} />
        </motion.div>

        {/* Floating particles */}
        {[...Array(8)].map((_, i) => <motion.div key={`particle-${i}`} className="absolute w-2 h-2 bg-blue-400/50 rounded-full" style={{
        left: `${20 + i * 10}%`,
        top: `${30 + i % 3 * 20}%`
      }} animate={{
        y: [0, -30, 0],
        opacity: [0.3, 0.8, 0.3],
        scale: [1, 1.5, 1]
      }} transition={{
        duration: 3 + i % 3,
        repeat: Infinity,
        ease: "easeInOut",
        delay: i * 0.3
      }} />)}
      </motion.div>
    </div>;
}
const FloatingStat = ({
  label,
  value,
  icon: Icon,
  positionClass,
  delay = 0
}: {
  label: string;
  value: string;
  icon: any;
  positionClass: string;
  delay?: number;
}) => {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <div ref={ref as any} className={`absolute ${positionClass} z-10 transition-all duration-1000 ease-out transform pointer-events-none`} style={{
    opacity: isVisible ? 1 : 0,
    transform: isVisible ? 'translateY(0)' : 'translateY(20px)',
    transitionDelay: `${delay}ms`
  }}>
      <div className="flex items-center gap-3 bg-black/40 backdrop-blur-md border border-white/10 px-5 py-3 rounded-full shadow-2xl">
        <div className="p-2 bg-blue-500/20 rounded-full text-blue-400">
          <Icon size={18} />
        </div>
        <div className="flex flex-col">
          <span className="text-xs text-blue-200/70 uppercase tracking-wider font-medium">{label}</span>
          <span className="text-lg font-light text-white tracking-tight font-mono">{value}</span>
        </div>
      </div>
    </div>;
};
export default function SensoryImmersion() {
  const {
    ref: titleRef,
    isVisible: titleVisible
  } = useRevealOnScroll();
  const {
    ref: ctaRef,
    isVisible: ctaVisible
  } = useRevealOnScroll();
  return <section id="sensory-immersion" className="dark bg-background text-foreground relative w-full min-h-screen overflow-hidden flex flex-col items-center justify-center py-24">
      {/* Dark background */}
      <div className="absolute inset-0 bg-black z-0"></div>
      
      {/* Ambient glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[80vw] h-[80vw] max-w-[800px] max-h-[800px] bg-blue-900/20 rounded-full blur-[120px] pointer-events-none z-0"></div>
      
      {/* Animated silk background */}
      <div className="absolute inset-0 z-1 w-full h-full">
        <AnimatedSilk />
      </div>

      {/* Content */}
      <div className="relative z-10 container mx-auto px-6 h-full flex flex-col items-center justify-center pointer-events-none">
        
        {/* Title section */}
        <div ref={titleRef as any} className={`text-center mb-12 transition-all duration-1000 ease-out transform ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <h2 className="text-sm md:text-base text-blue-400 font-medium tracking-[0.2em] uppercase mb-4">
            The Clean Air Bubble
          </h2>
          <h3 className="text-4xl md:text-6xl lg:text-7xl font-light text-white tracking-tighter mb-6">
            Sensory <span className="font-serif italic text-blue-100">Immersion</span>
          </h3>
          <p className="max-w-xl mx-auto text-gray-400 text-lg font-light leading-relaxed">
            Experience the feeling of pure air. A sanctuary of silence and sterility, 
            wrapped in a protective layer of positive pressure.
          </p>
        </div>

        {/* Floating stats */}
        <div className="absolute inset-0 w-full h-full pointer-events-none hidden md:block">
          <FloatingStat label="Air Stability" value="0.00% Turbulence" icon={Wind} positionClass="top-[20%] left-[10%] lg:left-[15%]" delay={200} />
          
          <FloatingStat label="Protection" value="Shielded by Pressure" icon={ShieldCheck} positionClass="top-[30%] right-[10%] lg:right-[15%]" delay={400} />
          
          <FloatingStat label="Purity Level" value="99.97% Filtration" icon={Droplets} positionClass="bottom-[25%] left-[12%] lg:left-[18%]" delay={600} />
          
          <FloatingStat label="Response Time" value="< 0.1ms Latency" icon={Zap} positionClass="bottom-[35%] right-[12%] lg:right-[18%]" delay={800} />
        </div>

        {/* Mobile stats */}
        <div className="grid grid-cols-1 gap-4 mt-8 md:hidden pointer-events-auto">
           <div className="flex items-center gap-3 bg-black/40 backdrop-blur-md border border-white/10 px-5 py-3 rounded-lg">
              <Wind className="text-blue-400 size-5" />
              <span className="text-white font-mono">0.00% Turbulence</span>
           </div>
           <div className="flex items-center gap-3 bg-black/40 backdrop-blur-md border border-white/10 px-5 py-3 rounded-lg">
              <ShieldCheck className="text-blue-400 size-5" />
              <span className="text-white font-mono">Shielded by Pressure</span>
           </div>
        </div>

        {/* CTA */}
        <div ref={ctaRef as any} className={`mt-16 pointer-events-auto transition-all duration-1000 delay-300 ease-out transform ${ctaVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <Link to="/WaitlistPage">
            <Button size="lg" className="rounded-full bg-white text-black hover:bg-blue-50 hover:text-blue-900 transition-all duration-300 px-8 py-6 text-lg font-medium tracking-wide shadow-[0_0_20px_rgba(255,255,255,0.3)] hover:shadow-[0_0_30px_rgba(59,130,246,0.5)]">
              Join the Waitlist
            </Button>
          </Link>
          <p className="mt-4 text-center text-xs text-gray-500 tracking-widest uppercase">
            Limited Pre-Order Availability
          </p>
        </div>

      </div>
    </section>;
}